import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import { Truck, Plus, Trash2, MapPin, Euro, Clock, Save, AlertCircle } from 'lucide-react';

export default function AdminDeliveryConfig() {
  const [config, setConfig] = useState(null);
  const [zones, setZones] = useState([]);
  const [venues, setVenues] = useState([]);
  const [prep, setPrep] = useState(null);
  const [savingPrep, setSavingPrep] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newZone, setNewZone] = useState({ kind: 'cap', label: '', cap: '', radius_km: 2, fee_eur: 3, eta_min: 30, min_order_eur: 0, active: true });

  const load = async () => {
    setLoading(true);
    try {
      const [cR, zR, vR] = await Promise.all([
        adminApi.get('/admin/delivery-config'),
        adminApi.get('/admin/delivery-zones'),
        adminApi.get('/admin/venues').catch(() => ({ data: { venues: [] } })),
      ]);
      setConfig(cR.data);
      setZones(zR.data || []);
      setVenues(vR.data?.venues || []);
      try {
        const pR = await adminApi.get('/admin/order-settings');
        setPrep(pR.data);
      } catch { setPrep({ prep_base_min: 15, prep_per_order_min: 4, prep_max_min: 60 }); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore caricamento'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendReportNow = async () => {
    setSendingReport(true);
    try {
      await adminApi.post('/kitchen/send-shift-report', {});
      toast.success('Report inviato via email');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invio non riuscito'); }
    finally { setSendingReport(false); }
  };

  const savePrep = async () => {
    setSavingPrep(true);
    try {
      const r = await adminApi.put('/admin/order-settings', {
        prep_base_min: parseInt(prep.prep_base_min) || 0,
        prep_per_order_min: parseInt(prep.prep_per_order_min) || 0,
        prep_max_min: parseInt(prep.prep_max_min) || 0,
        busy_extra_min: parseInt(prep.busy_extra_min) || 0,
        busy_auto_off_min: parseInt(prep.busy_auto_off_min) || 0,
        auto_hours: !!prep.auto_hours,
        shift_report_enabled: !!prep.shift_report_enabled,
        shift_report_time: prep.shift_report_time || '23:30',
        shift_report_email: prep.shift_report_email || '',
      });
      setPrep(r.data);
      toast.success('Tempi di preparazione salvati');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSavingPrep(false); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await adminApi.put('/admin/delivery-config', {
        enabled: !!config.enabled,
        min_order_eur: parseFloat(config.min_order_eur) || 0,
        cod_enabled: !!config.cod_enabled,
        default_fee_eur: parseFloat(config.default_fee_eur) || 0,
        max_radius_km: parseFloat(config.max_radius_km) || 10,
        origin_venue_id: config.origin_venue_id || null,
        hours: config.hours || [],
      });
      toast.success('Configurazione salvata');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const addZone = async () => {
    if (!newZone.label) { toast.error('Aggiungi un\'etichetta per la zona'); return; }
    if (newZone.kind === 'cap' && !newZone.cap) { toast.error('Inserisci il CAP'); return; }
    if (newZone.kind === 'radius' && !newZone.radius_km) { toast.error('Inserisci il raggio km'); return; }
    try {
      await adminApi.post('/admin/delivery-zones', {
        ...newZone,
        fee_eur: parseFloat(newZone.fee_eur) || 0,
        eta_min: parseInt(newZone.eta_min) || 30,
        min_order_eur: parseFloat(newZone.min_order_eur) || 0,
        radius_km: newZone.kind === 'radius' ? parseFloat(newZone.radius_km) : null,
        cap: newZone.kind === 'cap' ? newZone.cap.trim() : null,
      });
      toast.success('Zona aggiunta');
      setNewZone({ kind: 'cap', label: '', cap: '', radius_km: 2, fee_eur: 3, eta_min: 30, min_order_eur: 0, active: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const deleteZone = async (id) => {
    if (!window.confirm('Eliminare questa zona?')) return;
    try { await adminApi.delete(`/admin/delivery-zones/${id}`); toast.success('Zona eliminata'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  if (loading || !config) return <div className="p-8 flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  const originHasCoords = venues.some(v => v.address?.lat || v.address?.full || v.address?.street);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="admin-delivery-page">
      <PageTooltip page="delivery" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2"><Truck size={22} className="text-[#D44E20]" /> Consegna a domicilio</h1>
          <p className="text-sm text-[#64748B] mt-1">Attiva delivery, definisci zone (CAP o raggio km) e gestisci contrassegno</p>
        </div>
        <button onClick={saveConfig} disabled={saving} data-testid="save-delivery-config"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] transition-colors disabled:opacity-60">
          <Save size={14} /> {saving ? 'Salvataggio...' : 'Salva configurazione'}
        </button>
      </div>

      {/* Master toggle */}
      <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-[#1A1A1A]">Abilita servizio consegna</h3>
            <p className="text-xs text-[#64748B] mt-0.5">Quando spento, i clienti vedono solo l'opzione ritiro in locale.</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={!!config.enabled} onChange={e => setConfig({ ...config, enabled: e.target.checked })}
              data-testid="delivery-enabled-toggle" className="sr-only peer" />
            <div className="w-12 h-7 bg-slate-200 rounded-full peer peer-checked:bg-emerald-500 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-transform peer-checked:after:translate-x-5" />
          </label>
        </div>
      </div>

      {/* Kitchen prep-time ETA — applies to takeaway AND as the base for delivery */}
      {prep && (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm space-y-4" data-testid="prep-eta-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-[#1A1A1A] flex items-center gap-2"><Clock size={18} className="text-[#D44E20]" /> Tempo di preparazione (ETA cucina)</h3>
              <p className="text-xs text-[#64748B] mt-0.5">Stima mostrata al cliente nel tracking. Vale per il ritiro e come base per la consegna (a cui si somma il tempo di viaggio della zona).</p>
            </div>
            <button onClick={savePrep} disabled={savingPrep} data-testid="save-prep-eta"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] transition-colors disabled:opacity-60">
              <Save size={14} /> {savingPrep ? '...' : 'Salva'}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Tempo base (min)</label>
              <input type="number" min="0" value={prep.prep_base_min} onChange={e => setPrep({ ...prep, prep_base_min: e.target.value })}
                data-testid="prep-base-input" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none" />
              <p className="text-[11px] text-slate-400 mt-1">Cucina vuota</p>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">+ min per ordine in coda</label>
              <input type="number" min="0" value={prep.prep_per_order_min} onChange={e => setPrep({ ...prep, prep_per_order_min: e.target.value })}
                data-testid="prep-per-order-input" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none" />
              <p className="text-[11px] text-slate-400 mt-1">Ogni ordine attivo davanti</p>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Tetto massimo (min)</label>
              <input type="number" min="0" value={prep.prep_max_min} onChange={e => setPrep({ ...prep, prep_max_min: e.target.value })}
                data-testid="prep-max-input" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none" />
              <p className="text-[11px] text-slate-400 mt-1">Anche durante il picco</p>
            </div>
          </div>
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">🔥 Modalità "Occupato" (la cucina la attiva dal KDS)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#64748B] mb-1.5">Minuti extra quando occupato</label>
                <input type="number" min="0" value={prep.busy_extra_min ?? 20} onChange={e => setPrep({ ...prep, busy_extra_min: e.target.value })}
                  data-testid="busy-extra-input" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none" />
                <p className="text-[11px] text-slate-400 mt-1">Aggiunti all'attesa (es. 20/30)</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] mb-1.5">Auto-spegnimento dopo (min)</label>
                <input type="number" min="0" value={prep.busy_auto_off_min ?? 0} onChange={e => setPrep({ ...prep, busy_auto_off_min: e.target.value })}
                  data-testid="busy-autooff-input" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none" />
                <p className="text-[11px] text-slate-400 mt-1">0 = resta finché non la spegni</p>
              </div>
            </div>
            <label className="flex items-center gap-2.5 mt-4 cursor-pointer select-none" data-testid="auto-hours-toggle">
              <input type="checkbox" checked={!!prep.auto_hours} onChange={e => setPrep({ ...prep, auto_hours: e.target.checked })} className="rounded w-4 h-4 accent-[#D44E20]" />
              <span className="text-sm text-slate-700">🕒 Chiudi automaticamente gli ordini fuori dagli orari di apertura <span className="text-xs text-slate-400">(ritiro e delivery si aprono/chiudono da soli secondo gli orari del locale)</span></span>
            </label>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">📧 Report turno via email</p>
            <label className="flex items-center gap-2.5 mb-3 cursor-pointer select-none" data-testid="shift-report-toggle">
              <input type="checkbox" checked={!!prep.shift_report_enabled} onChange={e => setPrep({ ...prep, shift_report_enabled: e.target.checked })} className="rounded w-4 h-4 accent-[#D44E20]" />
              <span className="text-sm text-slate-700">Invia il riepilogo del turno a fine giornata</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#64748B] mb-1.5">Ora invio</label>
                <input type="time" value={prep.shift_report_time || '23:30'} onChange={e => setPrep({ ...prep, shift_report_time: e.target.value })}
                  data-testid="shift-report-time" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] mb-1.5">Email destinatario <span className="text-slate-400">(vuoto = titolare)</span></label>
                <input type="email" placeholder="titolare@esempio.it" value={prep.shift_report_email || ''} onChange={e => setPrep({ ...prep, shift_report_email: e.target.value })}
                  data-testid="shift-report-email" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#D44E20]/30 focus:border-[#D44E20] outline-none" />
              </div>
            </div>
            <button onClick={sendReportNow} disabled={sendingReport} data-testid="send-report-now"
              className="mt-3 text-sm font-bold px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-60">
              {sendingReport ? 'Invio...' : '📤 Invia report di prova ora'}
            </button>
            {prep.hist_prep_count >= 5 && (
              <p className="text-[11px] text-emerald-600 mt-2">✓ ETA cliente basata sullo storico reale (media {prep.hist_prep_avg} min su {prep.hist_prep_count} ordini)</p>
            )}
          </div>
        </div>
      )}

        <>
          {/* Global settings */}
          <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm space-y-4">
            <h3 className="text-base font-semibold text-[#1A1A1A]">Parametri globali</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Ordine minimo (€)</label>
                <input type="number" step="0.01" value={config.min_order_eur} onChange={e => setConfig({ ...config, min_order_eur: e.target.value })}
                  data-testid="min-order-input"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Fee default (€)</label>
                <input type="number" step="0.01" value={config.default_fee_eur} onChange={e => setConfig({ ...config, default_fee_eur: e.target.value })}
                  data-testid="default-fee-input"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Raggio massimo (km)</label>
                <input type="number" step="0.5" value={config.max_radius_km} onChange={e => setConfig({ ...config, max_radius_km: e.target.value })}
                  data-testid="max-radius-input"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
                <p className="text-[10px] text-[#64748B] mt-1">Oltre questo raggio il sistema blocca "fuori zona"</p>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Locale di partenza (raggio)</label>
                <select value={config.origin_venue_id || ''} onChange={e => setConfig({ ...config, origin_venue_id: e.target.value })}
                  data-testid="origin-venue-select"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none">
                  <option value="">Primo locale attivo</option>
                  {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                {!originHasCoords && (
                  <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1"><AlertCircle size={10}/> Configura l'indirizzo completo del locale per il calcolo raggio</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
              <input type="checkbox" id="cod" checked={!!config.cod_enabled} onChange={e => setConfig({ ...config, cod_enabled: e.target.checked })}
                data-testid="cod-toggle" className="w-4 h-4" />
              <label htmlFor="cod" className="text-sm text-[#1A1A1A] flex items-center gap-2 cursor-pointer">
                <Euro size={14}/> <b>Accetta pagamento alla consegna</b> <span className="text-[#64748B] text-xs">(contrassegno — rider incassa a domicilio)</span>
              </label>
            </div>
          </div>

          {/* Zones */}
          <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A1A1A]">Zone di consegna</h3>
              <span className="text-xs text-[#64748B]">{zones.length} zone configurate</span>
            </div>
            <p className="text-xs text-[#64748B]">I CAP hanno priorità sui raggi km. Se nessuna zona matcha, si usa la fee default sopra.</p>

            {/* Existing zones */}
            {zones.length > 0 && (
              <div className="space-y-2">
                {zones.map(z => (
                  <div key={z.id} data-testid={`zone-${z.id}`}
                    className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-2.5 border border-slate-200">
                    <div className="flex items-center gap-3 flex-1">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${z.kind === 'cap' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {z.kind === 'cap' ? `CAP ${z.cap}` : `≤ ${z.radius_km} km`}
                      </span>
                      <span className="text-sm font-semibold text-[#1A1A1A]">{z.label}</span>
                      <span className="text-xs text-[#64748B]">· {z.fee_eur.toFixed(2)}€ · {z.eta_min} min{z.min_order_eur > 0 ? ` · min ${z.min_order_eur.toFixed(2)}€` : ''}</span>
                    </div>
                    <button onClick={() => deleteZone(z.id)} data-testid={`delete-zone-${z.id}`}
                      className="text-red-400 hover:bg-red-50 w-8 h-8 rounded-lg flex items-center justify-center">
                      <Trash2 size={13}/>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add zone form */}
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <button onClick={() => setNewZone({ ...newZone, kind: 'cap' })} data-testid="zone-kind-cap"
                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${newZone.kind === 'cap' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-[#64748B]'}`}>
                  <MapPin size={12} className="inline mr-1"/> Zona per CAP
                </button>
                <button onClick={() => setNewZone({ ...newZone, kind: 'radius' })} data-testid="zone-kind-radius"
                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${newZone.kind === 'radius' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-[#64748B]'}`}>
                  <Clock size={12} className="inline mr-1"/> Zona per raggio km
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <input placeholder="Etichetta (es. Centro)" value={newZone.label} onChange={e => setNewZone({ ...newZone, label: e.target.value })}
                  data-testid="zone-label-input"
                  className="col-span-2 h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs" />
                {newZone.kind === 'cap' ? (
                  <input placeholder="CAP (00186)" value={newZone.cap} onChange={e => setNewZone({ ...newZone, cap: e.target.value })}
                    data-testid="zone-cap-input"
                    className="h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs" />
                ) : (
                  <input type="number" step="0.5" placeholder="km" value={newZone.radius_km} onChange={e => setNewZone({ ...newZone, radius_km: e.target.value })}
                    data-testid="zone-radius-input"
                    className="h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs" />
                )}
                <input type="number" step="0.5" placeholder="Fee €" value={newZone.fee_eur} onChange={e => setNewZone({ ...newZone, fee_eur: e.target.value })}
                  data-testid="zone-fee-input"
                  className="h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs" />
                <input type="number" placeholder="ETA min" value={newZone.eta_min} onChange={e => setNewZone({ ...newZone, eta_min: e.target.value })}
                  data-testid="zone-eta-input"
                  className="h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs" />
              </div>
              <div className="flex items-center gap-2">
                <input type="number" step="0.5" placeholder="Ordine minimo zona (opzionale)" value={newZone.min_order_eur} onChange={e => setNewZone({ ...newZone, min_order_eur: e.target.value })}
                  data-testid="zone-min-order-input"
                  className="flex-1 h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs" />
                <button onClick={addZone} data-testid="add-zone-btn"
                  className="flex items-center gap-1.5 px-4 h-9 rounded-lg text-xs font-bold text-white bg-[#D44E20] hover:bg-[#B3421A]">
                  <Plus size={12}/> Aggiungi zona
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
